import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const HeroSlideImage = ({ slide, active }) => {
  const [src, setSrc] = useState(slide.src);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(slide.src);
    setFailed(false);
  }, [slide.src]);

  if (failed) {
    return null;
  }

  const image = (
    <img
      src={src}
      alt=""
      className={`max-h-full max-w-full object-contain object-center drop-shadow-2xl transition-transform duration-[7000ms] ease-out ${
        active ? 'scale-105' : 'scale-100'
      }`}
      onError={() => {
        if (slide.fallbackSrc && src !== slide.fallbackSrc) {
          setSrc(slide.fallbackSrc);
          return;
        }
        setFailed(true);
        slide.onFailed?.();
      }}
    />
  );

  if (slide.framed) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 sm:p-6">
        <div className="flex h-full max-h-full w-full max-w-md items-center justify-center rounded-2xl bg-white/95 p-4 shadow-2xl sm:p-6">
          {image}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center">
      {image}
    </div>
  );
};

const HeroSlider = ({ slides = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [excludedKeys, setExcludedKeys] = useState(() => new Set());
  const [isPaused, setIsPaused] = useState(false);
  const slideSignature = slides.map((slide) => `${slide.key}:${slide.src}`).join('|');

  const visibleSlides = slides.filter((slide) => !excludedKeys.has(slide.key));

  useEffect(() => {
    setCurrentIndex(0);
    setExcludedKeys(new Set());
    setIsPaused(false);
  }, [slideSignature]);

  useEffect(() => {
    if (currentIndex >= visibleSlides.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, visibleSlides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
    setIsPaused(true);
  }, []);

  const goNext = useCallback(() => {
    if (visibleSlides.length <= 1) return;
    goToSlide((currentIndex + 1) % visibleSlides.length);
  }, [currentIndex, goToSlide, visibleSlides.length]);

  const goPrev = useCallback(() => {
    if (visibleSlides.length <= 1) return;
    goToSlide((currentIndex - 1 + visibleSlides.length) % visibleSlides.length);
  }, [currentIndex, goToSlide, visibleSlides.length]);

  useEffect(() => {
    if (visibleSlides.length <= 1 || isPaused) {
      return undefined;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % visibleSlides.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [visibleSlides.length, isPaused]);

  useEffect(() => {
    if (!isPaused) {
      return undefined;
    }

    const resumeTimer = setTimeout(() => setIsPaused(false), 12000);
    return () => clearTimeout(resumeTimer);
  }, [isPaused, currentIndex]);

  if (!visibleSlides.length) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-2xl border border-white/10 bg-neutral-900/60">
        <p className="text-sm text-gray-400">Design previews loading…</p>
      </div>
    );
  }

  const markFailed = (key) => {
    setExcludedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const showDotNav = visibleSlides.length <= 12;

  return (
    <div
      className="group relative h-full w-full overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {visibleSlides.map((slide, index) => (
        <div
          key={slide.key}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={index !== currentIndex}
        >
          <HeroSlideImage
            slide={{
              ...slide,
              onFailed: () => markFailed(slide.key),
            }}
            active={index === currentIndex}
          />
        </div>
      ))}

      {visibleSlides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous design"
            onClick={goPrev}
            className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-lg transition hover:bg-black/75 sm:left-3 sm:h-11 sm:w-11"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            aria-label="Next design"
            onClick={goNext}
            className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-lg transition hover:bg-black/75 sm:right-3 sm:h-11 sm:w-11"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {visibleSlides.length > 1 && (
        <div className="absolute bottom-3 left-1/2 z-10 flex max-w-[90%] -translate-x-1/2 items-center justify-center gap-3">
          {showDotNav ? (
            <div className="flex flex-wrap justify-center gap-2">
              {visibleSlides.map((slide, index) => (
                <button
                  key={`${slide.key}-dot`}
                  type="button"
                  aria-label={`Show design ${index + 1}`}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? 'w-6 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          ) : (
            <span className="rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white/90">
              {currentIndex + 1} / {visibleSlides.length}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default HeroSlider;
